import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import PageLoader from "../components/PageLoader";
import axiosClient from "../api/axiosClient";

export default function Statistics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");

  useEffect(() => {
    axiosClient
      .get("/stats/analytics")
      .then((res) => {
        // On force la conversion des strings en nombres
        const payload = res.data.data;
        
        if (payload) {
          // On s'assure que tous les champs numériques sont bien des nombres
          if (Array.isArray(payload.byCategory)) {
            payload.byCategory = payload.byCategory.map((c) => ({
              ...c,
              doc_count: Number(c.doc_count),
              downloads: Number(c.downloads),
              views: Number(c.views),
            }));
          }
          if (Array.isArray(payload.bySource)) {
            payload.bySource = payload.bySource.map((s) => ({
              ...s,
              doc_count: Number(s.doc_count),
              downloads: Number(s.downloads),
              views: Number(s.views),
            }));
          }
          if (Array.isArray(payload.topDocuments)) {
            payload.topDocuments = payload.topDocuments.map((d) => ({
              ...d,
              downloads: Number(d.downloads),
              views: Number(d.views),
            }));
          }
          if (Array.isArray(payload.timeline)) {
            payload.timeline = payload.timeline.map((t) => ({
              ...t,
              views: Number(t.views),
            }));
          }
        }
        
        setData(payload);
      })
      .catch((err) => console.error("Erreur stats:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <React.Fragment>
        <Header onSkin={setSkin} />
        <div className="main main-app cere-main p-3 p-lg-4">
          <PageLoader message="Calcul des statistiques…" />
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  // --- Filtre pour ne garder que les catégories avec des vues ---
  const categoriesWithViews = data.byCategory.filter((c) => c.views > 0);
  const sourcesWithViews = data.bySource; // On affiche tout

  // --- Timeline ---
  const timelineChart = {
    series: [{ name: "Vues", data: data.timeline.map((t) => t.views) }],
    options: {
      chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
      colors: ["#009e60"],
      fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 90, 100] } },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 3 },
      xaxis: {
        categories: data.timeline.map((t) =>
          new Date(t.date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" })
        ),
        labels: { style: { colors: "#6a7373" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: "#6a7373" } } },
      grid: { borderColor: "#e6e4de", strokeDashArray: 4 },
      tooltip: { theme: "light" },
    },
  };

  // --- Catégories (Donut) ---
  const categoryChart = {
    series: categoriesWithViews.map((c) => c.views),
    options: {
      labels: categoriesWithViews.map((c) => c.label),
      colors: ["#009e60", "#3a75c4", "#fcd116", "#0b1d1e", "#85b6fe", "#0dcaf0"],
      legend: { position: "bottom", fontSize: "13px", labels: { colors: "#2a3d3e" } },
      dataLabels: { enabled: true, style: { fontSize: "12px" } }, // On active les labels
      stroke: { width: 2, colors: ["#fff"] },
      plotOptions: { pie: { donut: { size: "70%" } } },
      tooltip: {
        y: {
          formatter: (val) => `${val} vue${val > 1 ? "s" : ""}`,
        },
      },
    },
  };

  // --- Top Documents (Barres) ---
  const topDocsChart = {
    series: [
      { name: "Vues", data: data.topDocuments.map((d) => d.views) },
      { name: "Téléchargements", data: data.topDocuments.map((d) => d.downloads) },
    ],
    options: {
      chart: { type: "bar", toolbar: { show: false }, stacked: true },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      colors: ["#3a75c4", "#009e60"],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.topDocuments.map((d) =>
          d.label.length > 30 ? d.label.slice(0, 30) + "…" : d.label
        ),
        labels: { style: { colors: "#6a7373" } },
      },
      yaxis: { labels: { style: { colors: "#2a3d3e", fontWeight: 500 } } },
      grid: { borderColor: "#e6e4de" },
      legend: { position: "top", horizontalAlign: "right" },
    },
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app cere-main p-3 p-lg-4">
        <div className="cere-page-head d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb cere-breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><Link to="/dashboard">Admin</Link></li>
                <li className="breadcrumb-item active">Statistiques</li>
              </ol>
            </nav>
            <h4 className="cere-page-title mb-0">Statistiques d'audience</h4>
            <p className="cere-page-subtitle mb-0">Analyse des vues et téléchargements</p>
          </div>
        </div>

        {/* KPI */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="4">
            <Card className="cere-card cere-kpi-card">
              <Card.Body>
                <div className="cere-kpi-icon cere-kpi-icon--green"><i className="ri-file-text-line"></i></div>
                <div><div className="cere-kpi-value">{data.totals.documents}</div><div className="cere-kpi-label">Documents</div></div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm="6" xl="4">
            <Card className="cere-card cere-kpi-card">
              <Card.Body>
                <div className="cere-kpi-icon cere-kpi-icon--blue"><i className="ri-eye-line"></i></div>
                <div><div className="cere-kpi-value">{data.totals.views}</div><div className="cere-kpi-label">Vues totales</div></div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm="6" xl="4">
            <Card className="cere-card cere-kpi-card">
              <Card.Body>
                <div className="cere-kpi-icon cere-kpi-icon--yellow"><i className="ri-download-line"></i></div>
                <div><div className="cere-kpi-value">{data.totals.downloads}</div><div className="cere-kpi-label">Téléchargements</div></div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Timeline */}
        <Card className="cere-card mb-4">
          <Card.Header className="cere-card-header">
            <Card.Title className="cere-card-title"><i className="ri-line-chart-line me-2"></i>Activité des 7 derniers jours (Vues)</Card.Title>
          </Card.Header>
          <Card.Body>
            {data.timeline.length > 0 ? (
              <ReactApexChart options={timelineChart.options} series={timelineChart.series} type="area" height={300} />
            ) : (
              <p className="text-muted text-center py-4">Pas encore de vues enregistrées.</p>
            )}
          </Card.Body>
        </Card>

        <Row className="g-3 mb-4">
          <Col lg="5">
            <Card className="cere-card h-100">
              <Card.Header className="cere-card-header">
                <Card.Title className="cere-card-title"><i className="ri-pie-chart-line me-2"></i>Vues par catégorie</Card.Title>
              </Card.Header>
              <Card.Body className="d-flex justify-content-center">
                {categoriesWithViews.length > 0 ? (
                  <ReactApexChart options={categoryChart.options} series={categoryChart.series} type="donut" height={320} />
                ) : (
                  <p className="text-muted text-center py-4">Aucune vue enregistrée pour le moment.</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg="7">
            <Card className="cere-card h-100">
              <Card.Header className="cere-card-header">
                <Card.Title className="cere-card-title"><i className="ri-building-bank-line me-2"></i>Performance par source</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="cere-table-docs mb-0">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th className="text-center">Docs</th>
                        <th className="text-center">Vues</th>
                        <th className="text-center">Téléch.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sourcesWithViews.map((s, i) => (
                        <tr key={i}>
                          <td className="fw-medium">{s.label}</td>
                          <td className="text-center">{s.doc_count}</td>
                          <td className="text-center">{s.views}</td>
                          <td className="text-center">{s.downloads}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Top Docs */}
        <Card className="cere-card mb-4">
          <Card.Header className="cere-card-header">
            <Card.Title className="cere-card-title"><i className="ri-bar-chart-horizontal-line me-2"></i>Top 8 des documents</Card.Title>
          </Card.Header>
          <Card.Body>
            {data.topDocuments.length > 0 ? (
              <ReactApexChart options={topDocsChart.options} series={topDocsChart.series} type="bar" height={350} />
            ) : <p className="text-muted text-center py-4">Aucun document.</p>}
          </Card.Body>
        </Card>

        <Footer />
      </div>
    </React.Fragment>
  );
}